// PMS reservation domain — statuses, sources, guarantees, the state machine
// (docs/PMS-BRIEF.md §5.2). The server RPC (pms_reservation_transition) is the
// enforcer; this map is the CLIENT mirror used to decide which action buttons
// to show. Keep the two in lockstep.

import type {
  AppliesTo,
  BedLayout,
  Cents,
  IsoDate,
  KitchenKind,
  NightPrice,
  Product,
  ProductRequest,
  SpaceKind,
  UnitKind,
} from './types.js';

export const PMS_RESERVATION_STATUSES = [
  'reserved',
  'confirmed',
  'checked_in',
  'checked_out',
  'cancelled',
] as const;
export type PmsReservationStatus = (typeof PMS_RESERVATION_STATUSES)[number];

export const PMS_SOURCES = ['channex', 'direct', 'manual'] as const;
export type PmsSource = (typeof PMS_SOURCES)[number];

export const PMS_GUARANTEES = ['none', 'card_on_file', 'vad_pending', 'vad_done', 'transfer_pending', 'transfer_done'] as const;
export type PmsGuarantee = (typeof PMS_GUARANTEES)[number];

export const PMS_BOARDS = ['none', 'bb', 'hb'] as const;
export type PmsBoard = (typeof PMS_BOARDS)[number];

export const PMS_TRANSITION_ACTIONS = [
  'confirm',
  'unconfirm',
  'checkin',
  'undo_checkin',
  'checkout',
  'cancel',
  'no_show',
] as const;
export type PmsTransitionAction = (typeof PMS_TRANSITION_ACTIONS)[number];

/** action → { from: allowed origin statuses, to: resulting status } — §5.2. */
export const PMS_TRANSITIONS: Record<
  PmsTransitionAction,
  { from: readonly PmsReservationStatus[]; to: PmsReservationStatus }
> = {
  confirm: { from: ['reserved'], to: 'confirmed' },
  unconfirm: { from: ['confirmed'], to: 'reserved' },
  checkin: { from: ['confirmed'], to: 'checked_in' },
  undo_checkin: { from: ['checked_in'], to: 'confirmed' },
  checkout: { from: ['checked_in'], to: 'checked_out' },
  cancel: { from: ['reserved', 'confirmed'], to: 'cancelled' },
  no_show: { from: ['confirmed'], to: 'cancelled' },
};

/**
 * Actions the database REFUSES without a reason (migration 0077).
 *
 * Declared as data rather than hard-coded at the call site so the form, the
 * button and any future caller read the same list — and so adding a second
 * reason-bearing action is one line here instead of a hunt. The enforcement is
 * still the RPC's: this set exists to ask the operator BEFORE the round trip,
 * never to be the thing that decides.
 *
 * Undoing a check-in is the only one today. Walking `confirmed → reserved` back
 * is ordinary desk work — an option lapses, a guarantee falls through — but a
 * check-in asserts a person physically arrived, so unwinding it should say why.
 */
export const PMS_ACTIONS_NEEDING_REASON: readonly PmsTransitionAction[] = ['undo_checkin'];

export function pmsActionNeedsReason(action: PmsTransitionAction): boolean {
  return PMS_ACTIONS_NEEDING_REASON.includes(action);
}

/** Actions legal from a given status (drives the UI buttons). */
export function pmsActionsFrom(status: PmsReservationStatus): PmsTransitionAction[] {
  return PMS_TRANSITION_ACTIONS.filter((a) => PMS_TRANSITIONS[a].from.includes(status));
}

/**
 * The acts that apply to a BOOKING rather than to one room — migration 0091's
 * split, named once so the UI reads it instead of restating it.
 *
 * **Commercial acts fan out; physical acts name their room.** Confirming is an
 * act on an agreement: an option lapses for the whole booking, not for room
 * 302. Arriving is physical — a person walks into one room. So
 * `pms_reservation_transition` applies these three across every logement and
 * REFUSES the rest when a booking holds several, which is what the buttons
 * must agree with: offering « Check-in » on a two-room booking would be
 * offering something the database will decline.
 */
export const PMS_BOOKING_WIDE_ACTIONS: readonly PmsTransitionAction[] = [
  'confirm',
  'unconfirm',
  'cancel',
];

export function pmsActionIsBookingWide(action: PmsTransitionAction): boolean {
  return PMS_BOOKING_WIDE_ACTIONS.includes(action);
}

/**
 * What may still be changed, FIELD BY FIELD (migration 0078, operator D2).
 *
 * This used to be one boolean for the whole reservation, and it returned true
 * for `checked_in` — so the arrival date of a guest already in the room was
 * freely rewritable, with no rule anywhere saying whether that was intended.
 * The operator spotted the missing rationale on 2026-08-11; the rule below is
 * the answer, and `pms_update_reservation` enforces the same one.
 *
 * `checkout: 'extend_only'` is the shape a boolean could not carry, and it is
 * the reason this became a record: a stay may be lengthened while it runs, but
 * SHORTENING one is an early departure — money kept for nights not slept, which
 * the operator has placed in the books as a non-sale bearing no TVA. That is an
 * accounting act, not a date edit, and it gets a home with the folio work.
 */
export type PmsCheckoutRule = 'free' | 'extend_only';

export interface PmsEditRule {
  /** May the reservation be opened for editing at all. */
  editable: boolean;
  /** May the ARRIVAL date move. False once it has happened: the desk observed
   *  it and the event log dated it, so the row must not disagree with them. */
  checkin: boolean;
  checkout: PmsCheckoutRule;
  /** Occupants, guest identity and price stay correctable in every editable
   *  state — a misspelt name or a wrong rate must never be hard to fix, and the
   *  night rows void-and-append, so a price change leaves its own trail. */
  guest: boolean;
  price: boolean;
}

const LOCKED: PmsEditRule = {
  editable: false,
  checkin: false,
  checkout: 'extend_only',
  guest: false,
  price: false,
};

export function pmsEditRule(status: PmsReservationStatus): PmsEditRule {
  switch (status) {
    case 'reserved':
    case 'confirmed':
      return { editable: true, checkin: true, checkout: 'free', guest: true, price: true };
    case 'checked_in':
      return { editable: true, checkin: false, checkout: 'extend_only', guest: true, price: true };
    default:
      return LOCKED; // checked_out, cancelled — terminal since P1
  }
}

/** True when a reservation may be opened for editing at all. Kept as the
 *  coarse question (does the ✎ button appear); `pmsEditRule` answers the
 *  per-field one. */
export function pmsIsEditable(status: PmsReservationStatus): boolean {
  return pmsEditRule(status).editable;
}

// Staff-facing labels — French (repo R4), taken from the standard FR hotel
// vocabulary (Opera/Mews FR, and what Vega shows the desk today) rather than
// coined here: the team migrating off Vega should not have to translate.
//
// `checked_in` is « Arrivée effectuée », NOT « En séjour », and that is a
// deliberate reservation of a name. The operator draws a line the standard
// blurs: in-house should mean PAST THE FIRST NIGHT, while checked_in is the
// provisional state on arrival — a guest can go up to the room and come back
// down to leave without staying. When that state is modelled, « En séjour » is
// the label it needs, so it is not spent on checked_in now.
export const PMS_STATUS_LABELS_FR: Record<PmsReservationStatus, string> = {
  reserved: 'Réservée',
  confirmed: 'Confirmée',
  checked_in: 'Arrivée effectuée',
  checked_out: 'Départ effectué',
  cancelled: 'Annulée',
};

// « Annuler » is taken by the cancel ACTION, so the fall-backs are named for
// what they undo rather than borrowing that word — « Annuler l'arrivée » next to
// « Annuler » on the same row would be two very different acts one word apart.
export const PMS_ACTION_LABELS_FR: Record<PmsTransitionAction, string> = {
  confirm: 'Confirmer',
  unconfirm: 'Retour en réservée',
  checkin: 'Check-in',
  undo_checkin: "Annuler l'arrivée",
  checkout: 'Check-out',
  cancel: 'Annuler',
  no_show: 'No-show',
};

/**
 * What the DESK is told a multi-room booking is doing — the label 0091
 * deliberately did not make a status.
 *
 * The database derives `pms_reservation.status` into the same five values a
 * single-room booking has always had, because a sixth would have changed a
 * CHECK constraint, the filters and the meaning of every reservation row to
 * say something only a screen says. This is that screen saying it.
 *
 * « Partiellement arrivée » is the case the operator will actually meet: two
 * rooms, one guest upstairs and one still to come. The booking reads
 * `checked_in` — correct, and not the whole story — and a desk that could not
 * see the difference would go looking for a guest who has not arrived.
 *
 * Returns null when there is nothing partial to say, so the caller falls back
 * to `PMS_STATUS_LABELS_FR` and no single-room booking is ever labelled
 * differently from how it is labelled today.
 */
export function pmsPartialStatusLabelFr(
  stays: readonly { status: PmsReservationStatus; cancelledAt: string | null }[],
): string | null {
  const live = stays.filter((s) => s.cancelledAt === null);
  if (live.length < 2) return null; // one room cannot be partial

  const n = (status: PmsReservationStatus) => live.filter((s) => s.status === status).length;
  const arrived = n('checked_in');
  const departed = n('checked_out');

  if (arrived > 0 && arrived + departed < live.length) return 'Partiellement arrivée';
  if (departed > 0 && departed < live.length) return 'Partiellement repartie';
  return null;
}

/** How many of a booking's logements are still live — what a desk counts when
 *  it asks « how many rooms is this? ». Released rooms are excluded: they hold
 *  no inventory and their charges survive only for the invoice. */
export function pmsLiveRoomCount(
  stays: readonly { cancelledAt: string | null }[],
): number {
  return stays.filter((s) => s.cancelledAt === null).length;
}

export const PMS_SOURCE_LABELS_FR: Record<PmsSource, string> = {
  channex: 'Channex (OTA)',
  direct: 'Site (direct)',
  manual: 'Saisie manuelle',
};

// ── Booking origin — the Planning's five marks (operator, 2026-09-30) ────────
//
// A DISPLAY bucket, derived and never stored. The three facts it reads stay
// orthogonal in the data (0137): the mechanism (`source`), the OTA (`channel`)
// and the counterparty (`bookingPartyCode`). The desk asked to SEE five
// classes; collapsing them here, in one pure function, keeps that wish from
// leaking back into the schema.
//
// The rules, in order:
//   booking   channel « booking.com » — whatever the mechanism, a desk-typed
//             mirror of an OTA booking is still that OTA's customer;
//   expedia   channel « expedia »;
//   direct    our own engine: `source = 'direct'` (the seed's BOOKING BUTTON),
//             or the form's « SiteMinder direct-book » channel;
//   manual    typed at the desk: phone, walk-in, AND agencies / tour operators
//             (operator: « Agencies → Manuel ») — the seed puts an unmapped
//             agency's Vega source name in `channel`, and it is still desk work;
//   other     an OTA we cannot name: a channel-manager booking with no message
//             (« SITE MINDER »), Airbnb.

export const PMS_BOOKING_ORIGINS = ['booking', 'expedia', 'direct', 'manual', 'other'] as const;
export type PmsBookingOrigin = (typeof PMS_BOOKING_ORIGINS)[number];

/** OTAs the desk may record by hand (the form's « Canal » list) that none of
 *  the named buckets claims. A manual booking carrying one is an OTA booking
 *  typed in, not desk business. */
const OTHER_OTA_CHANNELS: readonly string[] = ['airbnb'];

export function pmsBookingOrigin(r: { source: PmsSource; channel: string | null }): PmsBookingOrigin {
  const ch = (r.channel ?? '').trim().toLowerCase();
  if (ch === 'booking.com') return 'booking';
  if (ch === 'expedia') return 'expedia';
  if (r.source === 'direct' || ch === 'siteminder-direct') return 'direct';
  if (r.source === 'manual' && !OTHER_OTA_CHANNELS.includes(ch)) return 'manual';
  return 'other';
}

export const PMS_BOOKING_ORIGIN_LABELS_FR: Record<PmsBookingOrigin, string> = {
  booking: 'Booking.com',
  expedia: 'Expedia',
  direct: 'Direct (site)',
  manual: 'Manuel',
  other: 'Autre',
};

/** The one letter the Planning prints on the bar's origin tab. A letter and not
 *  only a hue: the fill already carries Vega's four status colours, and a mark
 *  that relied on colour alone would be unreadable to a colour-blind agent. */
export const PMS_BOOKING_ORIGIN_LETTERS: Record<PmsBookingOrigin, string> = {
  booking: 'B',
  expedia: 'E',
  direct: 'D',
  manual: 'M',
  other: 'A',
};

export const PMS_GUARANTEE_LABELS_FR: Record<PmsGuarantee, string> = {
  none: 'Sans garantie',
  card_on_file: 'Carte enregistrée',
  vad_pending: 'VAD à traiter',
  vad_done: 'VAD traitée',
  transfer_pending: 'Virement à recevoir',
  transfer_done: 'Virement reçu',
};

/** Nights in [checkin, checkout) — ISO date strings, timezone-free math. */
export function pmsNightCount(checkinIso: string, checkoutIso: string): number {
  const a = Date.UTC(
    Number(checkinIso.slice(0, 4)),
    Number(checkinIso.slice(5, 7)) - 1,
    Number(checkinIso.slice(8, 10)),
  );
  const b = Date.UTC(
    Number(checkoutIso.slice(0, 4)),
    Number(checkoutIso.slice(5, 7)) - 1,
    Number(checkoutIso.slice(8, 10)),
  );
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

// ── The unit model (P1.6) ────────────────────────────────────────────────────

export const UNIT_KIND_LABELS_FR: Record<UnitKind, string> = {
  room: 'Chambre',
  studio: 'Studio',
  apartment: 'Appartement',
  villa: 'Villa',
};

export const SPACE_KIND_LABELS_FR: Record<SpaceKind, string> = {
  bedroom: 'Chambre',
  living: 'Séjour',
  other: 'Autre pièce',
};

export const KITCHEN_LABELS_FR: Record<KitchenKind, string> = {
  none: 'Sans cuisine',
  kitchenette: 'Kitchenette',
  full: 'Cuisine équipée',
};

/** True when this unit's beds are described space by space rather than by its
 *  own arrangements. The XOR the database enforces, read from the domain side:
 *  a unit has either achievable configurations or spaces, never both and never
 *  neither.
 *
 *  ⚠ The discriminator moved in 0173/0175, from « `bedConfig` is null » to
 *  « the option set is empty ». Same rule, stated on the table that now owns
 *  it — `pms_room.bed_config` no longer exists. */
export function pmsIsMultiSpace(unit: {
  unitKind: UnitKind;
  configOptions: readonly string[];
}): boolean {
  return unit.configOptions.length === 0;
}

/**
 * How many people the BEDS sleep, summed over a unit's spaces.
 *
 * Deliberately NOT `max_occupancy`, and deliberately never written to it.
 * Booking.com lets a 4-bed apartment cap at 5 with an extra bed, or at 4
 * despite a sofa bed, so the two are different facts about the same unit
 * (design brief §6.5). This one drives a WARNING in the fiche; the operator's
 * figure stands.
 *
 * A double sleeps 2, a single sleeps 1 — the same reading `bedLayoutRank` uses.
 * Whether that double is a Queen or a King does not change the count, which is
 * one reason 0173 could move the bed TYPE off the arrangement without touching
 * this function's arithmetic.
 *
 * Alternative arrangements are ignored by construction: a room's other
 * configurations describe the SAME mattresses rearranged, not extra capacity,
 * so this sums one space's beds and never a room's option set.
 */
export function pmsBedCapacity(
  beds: readonly { bedConfig: string; quantity: number }[],
  layoutOf: (code: string) => BedLayout | null,
): number {
  let sleeps = 0;
  for (const bed of beds) {
    const layout = layoutOf(bed.bedConfig);
    if (layout === null) continue; // an unknown code is not a guess — Hard Rule 1
    sleeps += (layout.doubles * 2 + layout.singles) * bed.quantity;
  }
  return sleeps;
}


// ── The charge model's functional core (charge brief §3.2, §3.3) ─────────────
//
// Pure functions over plain data, per ARCHITECTURE.md: the TypeScript domain is
// a functional core, never an invariant enforcer. THE DATABASE IS AUTHORITATIVE
// — `pms_write_charges` writes the real lines and returns the real total, and
// what these produce is a PREVIEW the desk sees while typing.
//
// That makes them a deliberate second implementation of one rule, which is a
// cost worth naming: a form that could not total a stay before saving it would
// make the operator save to find out. They are kept honest by being tested
// against the same worked example the migration's own probe asserts (a 3-night
// stay at 100/120/95 with a per-night extra and a tax tariff), so a divergence
// shows up as a failing test rather than as a wrong number at a desk.

/** The dates a product bills on, for a stay [checkin, checkout).
 *
 *  `each_night` excludes the departure day; `each_morning` excludes the arrival
 *  day. They are NOT the same count shifted by one — they select different
 *  dates, which is exactly where a naive implementation lands one line too
 *  many. Mirrors `pms_product_dates` (0084). */
export function pmsProductDates(
  appliesTo: AppliesTo,
  checkinIso: IsoDate,
  checkoutIso: IsoDate,
): IsoDate[] {
  const nights = pmsNightCount(checkinIso, checkoutIso);
  if (nights <= 0) return [];
  switch (appliesTo) {
    case 'arrival':
      return [checkinIso];
    case 'departure':
      return [checkoutIso];
    case 'each_night':
      return Array.from({ length: nights }, (_, i) => pmsAddDays(checkinIso, i));
    case 'each_morning':
      return Array.from({ length: nights }, (_, i) => pmsAddDays(checkinIso, i + 1));
  }
}

/** `YYYY-MM-DD` + n days, in UTC so no timezone can shift a civil date. */
export function pmsAddDays(iso: IsoDate, days: number): IsoDate {
  const d = new Date(
    Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10))),
  );
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

// ── The ARI window (P2 channel sync) ────────────────────────────────────────
//
// How far ahead we publish availability and rates, in ONE place. It was a
// private `horizon()` in the sync composition root, reachable only by the cron
// job handler; the operator-fired sync needed the same window, and a second
// copy of "365 days from today" is a copy that drifts — the manual sync would
// have covered a different span than the automatic one, and the reconcile pass
// would have reported the difference as a discrepancy.

/** How far ahead availability and rates are published to the channel.
 *
 *  500 since CC6 (PMS-CHANNEX-CERTIFICATION-BRIEF §3.3): Channex's full sync
 *  is 500 days (certification test 1). Mirrored in SQL by
 *  `pms_ari_horizon_days()`, which clips the ARI outbox to the same window —
 *  change both or neither. */
export const PMS_ARI_HORIZON_DAYS = 500;

/**
 * Today's civil date in Europe/Paris.
 *
 * NOT `new Date().toISOString().slice(0, 10)`, which is what the composition
 * root did: between midnight and 02:00 Paris in summer that is YESTERDAY, so
 * the window opened on a date already past and pushed availability for a day
 * the channel can no longer sell. Mirrors staff `todayIsoParis` — `en-CA`
 * because it formats as `YYYY-MM-DD`.
 */
export function pmsTodayParis(now: Date = new Date()): IsoDate {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(now);
}

/** The rolling window an ARI push covers: today (Paris) → today + horizon. */
export function pmsAriWindow(
  todayIso: IsoDate = pmsTodayParis(),
  days: number = PMS_ARI_HORIZON_DAYS,
): { from: IsoDate; to: IsoDate } {
  return { from: todayIso, to: pmsAddDays(todayIso, days) };
}

/**
 * The per-night vector for a stay, PRESERVING what the desk already typed.
 *
 * The preservation is the whole point. Extending a stay by one night must not
 * flatten the four nights already priced at four different figures — which is
 * what a naive "rebuild from the fallback" would do, silently, at the moment
 * the operator is least likely to re-read the breakdown.
 */
export function pmsNightVector(
  checkinIso: IsoDate,
  checkoutIso: IsoDate,
  existing: readonly NightPrice[],
  fallbackCents: Cents,
): NightPrice[] {
  // The quantity is kept with the price: a per-person night re-cut for new dates
  // is still per person, and dropping it would halve the room without a word.
  const kept = new Map(existing.map((n) => [n.date, n]));
  return pmsProductDates('each_night', checkinIso, checkoutIso).map((date) => {
    const k = kept.get(date);
    if (k === undefined) return { date, priceCents: fallbackCents };
    return k.quantity === undefined || k.quantity === 1
      ? { date, priceCents: k.priceCents }
      : { date, priceCents: k.priceCents, quantity: k.quantity };
  });
}

/** What a night costs: its price times its units (`NightPrice.quantity`, one room when absent). */
export const pmsNightCents = (n: NightPrice): Cents => n.priceCents * (n.quantity ?? 1);

/**
 * How a room line's quantity reads, for the form and the planning pane.
 *
 * A room line is normally ONE room. Vega keeps a group's charges and its rooming
 * list in two places it never checks against each other (operator, 2026-10-22), so
 * a room line can carry more:
 *   · `perPerson` — the quantity is the room's head count: a group priced per
 *     person (« DEMI PENSION GROUPE », one line per room with its heads), shown
 *     « 75,00 /pers. »;
 *   · `rooms` — any other quantity above 1: several rooms Vega billed sit on this
 *     logement, shown « 110,00 ×5 ».
 * The two cannot be told apart when a lump of N rooms lands on a room of N persons;
 * the TOTAL is right either way (price × quantity), only the wording is a reading.
 * A one-person room priced per person reads as `room`: the figure is the same.
 */
export type PmsRoomLineUnits =
  | { kind: 'room' }
  | { kind: 'perPerson'; persons: number }
  | { kind: 'rooms'; rooms: number };

export function pmsRoomLineUnits(quantity: number | undefined, heads: number): PmsRoomLineUnits {
  const q = quantity ?? 1;
  if (q <= 1) return { kind: 'room' };
  return q === heads ? { kind: 'perPerson', persons: q } : { kind: 'rooms', rooms: q };
}

export interface PmsChargePreview {
  roomCents: Cents;
  productCents: Cents;
  /** Collected on behalf of the commune — NOT revenue. Shown as its own line
   *  because a guest who cannot see the taxe de séjour asks about it at the
   *  desk (brief §8 step 5). */
  taxCents: Cents;
  /** What the guest owes: everything, tax included. */
  totalCents: Cents;
  /** Persons the tax was computed on — adults. Under-18s are exempt. */
  personsTaxable: number;
  personsExempt: number;
}

/**
 * What a stay will cost, previewed. Mirrors `pms_write_charges`.
 *
 * `products` is the set the desk ASKED for; every active auto-apply product is
 * added on top, because « appliquer automatiquement » is the difference
 * between offered and always billed and a preview that omitted them would
 * under-quote every stay.
 */
export function pmsPreviewCharges(input: {
  checkin: IsoDate;
  checkout: IsoDate;
  nights: readonly NightPrice[];
  adults: number;
  children: number;
  catalogue: readonly Product[];
  requested: readonly ProductRequest[];
}): PmsChargePreview {
  const { checkin, checkout, adults, children } = input;
  const occupants = Math.max(adults + children, 1);

  const roomCents = input.nights.reduce((sum, n) => sum + pmsNightCents(n), 0);

  let productCents = 0;
  for (const p of input.catalogue) {
    if (!p.active || p.isTax) continue;
    const asked = input.requested.find((r) => r.productId === p.id);
    if (asked === undefined && !p.autoApply) continue;
    const units = Math.max(asked?.quantity ?? 1, 1) * (p.perPerson ? occupants : 1);
    productCents += units * p.priceCents * pmsProductDates(p.appliesTo, checkin, checkout).length;
  }

  // Adults only: `children` means « under 18 » (a TAX definition, not an
  // occupancy one) and they are exempt.
  let taxCents = 0;
  if (adults > 0) {
    const nightCount = pmsProductDates('each_night', checkin, checkout).length;
    for (const p of input.catalogue) {
      if (!p.active || !p.isTax) continue;
      taxCents += adults * p.priceCents * nightCount;
    }
  }

  return {
    roomCents,
    productCents,
    taxCents,
    totalCents: roomCents + productCents + taxCents,
    personsTaxable: adults,
    personsExempt: children,
  };
}

// ── Channex identifiers (P2 onboarding) ──────────────────────────────────────
//
// A Channex room type / rate plan / property id is a UUID, and RFC 4122 makes
// UUID comparison CASE-INSENSITIVE. The webhook learned that the expensive way
// on 2026-08-17: routing keyed on a byte-exact string answered « établissement
// non configuré » for the uppercase form of an id it accepted in lowercase, and
// a 503 is indistinguishable from a missing variable.
//
// So every Channex id entering this system is normalised at the boundary, once,
// here — rather than at each place that compares one.

const CHANNEX_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Trim and lowercase a Channex id. Blank means UNMAPPED, which is a real and
 *  expected state — a property is onboarded one room type at a time — so it is
 *  `null` rather than an error. */
export function pmsNormalizeChannexId(raw: string | null | undefined): string | null {
  const v = (raw ?? '').trim().toLowerCase();
  return v === '' ? null : v;
}

/** Is this something Channex could have issued? `null` (unmapped) passes.
 *
 *  Worth checking at the point of entry rather than trusting the paste: the
 *  failure of a wrong value is a booking that dead-letters days later, and the
 *  most likely wrong value is a room type's NAME, which looks nothing like a
 *  UUID and would otherwise be stored without complaint. */
export function pmsIsChannexId(value: string | null): boolean {
  return value === null || CHANNEX_ID_RE.test(value);
}
